import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { RefreshCw } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useFraudOverview } from '@/lib/api/fraud-queries'
import type { FraudAssessment, FraudOverviewTiles, FraudRankedUser } from '@/lib/api/fraud-types'
import { cn } from '@/lib/utils'
import { Banner, DecisionChip, ModeChip, OutcomeChip, ScoreBadge, SignalList, Spinner, UserLink } from './shared'
import { MODE_META, ROW, TD, TH, kindLabel, moneyFormatter, relativeTime } from './format'

const WINDOWS = [
  { value: '15m', label: '15 min' },
  { value: '60m', label: '1 hour' },
  { value: '360m', label: '6 hours' },
  { value: '1440m', label: '24 hours' },
] as const

function Tile({ label, value, sub, tone }: { label: string; value: string | number; sub?: string; tone?: 'warn' | 'bad' }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">{label}</p>
        <p className={cn('text-2xl font-bold mt-1', tone === 'bad' ? 'text-red-600 dark:text-red-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : 'text-neutral-900 dark:text-white')}>
          {value}
        </p>
        {sub && <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  )
}

function tiles(t: FraudOverviewTiles, money: (v: number) => string) {
  return [
    { label: 'Card reloads', value: t.card_reloads, sub: `${money(t.card_reload_amount)} · ${t.card_reload_users} attendees` },
    { label: 'Decisions', value: t.assessments, sub: `${t.step_ups} step-ups (${t.step_ups_enforced} enforced) · ${t.three_ds_passed} passed 3DS` },
    { label: 'Refused', value: t.refused, sub: `${t.declines} declined by policy · ${t.blocked_attempts} blocked`, tone: t.refused ? 'warn' as const : undefined },
    { label: 'Provider declines', value: t.provider_declined, sub: 'Cards refused by the processor', tone: t.provider_declined >= 5 ? 'warn' as const : undefined },
    { label: 'Flagged attendees', value: t.flagged_users, sub: `${t.blocked_users} currently blocked · ${t.trusted_users ?? 0} trusted${t.trusted_cards ? ` · ${t.trusted_cards} trusted card${t.trusted_cards === 1 ? '' : 's'}` : ''}`, tone: t.blocked_users ? 'bad' as const : t.flagged_users ? 'warn' as const : undefined },
    { label: 'Chargebacks', value: t.chargebacks, sub: `${t.chargebacks_open} open · ${t.chargebacks_lost} lost · ${money(t.chargeback_amount)}`, tone: t.chargebacks_lost ? 'bad' as const : undefined },
  ]
}

export function OverviewTab({ eventId, currency }: { eventId: number; currency?: string | null }) {
  const [window, setWindow] = useState<(typeof WINDOWS)[number]['value']>('60m')
  const { data, isLoading, isFetching, error, dataUpdatedAt, refetch } = useFraudOverview(eventId, window)
  const money = moneyFormatter(data?.currency || currency)

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-neutral-200 dark:border-neutral-800 overflow-hidden">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setWindow(w.value)}
              className={cn('px-3 py-1.5 text-sm', window === w.value
                ? 'bg-blue-600 text-white'
                : 'bg-white dark:bg-neutral-950 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800')}
            >
              {w.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-xs text-neutral-500 dark:text-neutral-400">
          {dataUpdatedAt ? <span>Updated {relativeTime(new Date(dataUpdatedAt).toISOString())}</span> : null}
          <button onClick={() => refetch()} className="inline-flex items-center gap-1 hover:text-neutral-900 dark:hover:text-white" disabled={isFetching}>
            <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} /> Refresh
          </button>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-red-600 dark:text-red-400">Failed to load fraud overview: {error.message}</p>
      ) : isLoading || !data ? (
        <div className="flex items-center justify-center py-16"><Spinner /></div>
      ) : (
        <>
          {data.policy.degraded && (
            <Banner kind="error">
              The fraud policy could not be read from the database. Charges are assessed in shadow on platform code defaults; nothing is enforced.
            </Banner>
          )}
          <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 text-sm">
            <ModeChip mode={data.policy.mode} />
            <span className="text-neutral-600 dark:text-neutral-400">{MODE_META[data.policy.mode]?.description}</span>
            <Link to="/events/$eventId/fraud" params={{ eventId: String(eventId) }} search={{ tab: 'policy' }} className="ml-auto text-blue-600 dark:text-blue-400 hover:underline">
              Policy
            </Link>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            {tiles(data.tiles, money).map((tile) => <Tile key={tile.label} {...tile} />)}
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
            <Card className="xl:col-span-3">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-base">Highest risk attendees</CardTitle>
                <Link to="/events/$eventId/fraud" params={{ eventId: String(eventId) }} search={{ tab: 'users' }} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">
                  All attendees
                </Link>
              </CardHeader>
              <CardContent className="overflow-x-auto px-0">
                <TopUsersTable eventId={eventId} rows={data.top_users} money={money} />
              </CardContent>
            </Card>
            <Card className="xl:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Latest decisions</CardTitle>
              </CardHeader>
              <CardContent className="px-0">
                <Feed eventId={eventId} rows={data.feed} money={money} />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

export function TopUsersTable({ eventId, rows, money }: { eventId: number; rows: FraudRankedUser[]; money: (v: number | string) => string }) {
  if (rows.length === 0) {
    return <p className="px-6 pb-4 text-sm text-neutral-500 dark:text-neutral-400">No card activity at this event yet.</p>
  }
  return (
    <table className="w-full whitespace-nowrap">
      <thead>
        <tr className="border-b border-neutral-200 dark:border-neutral-800">
          <th className={TH}>Attendee</th>
          <th className={TH}>Score</th>
          <th className={TH}>Next charge</th>
          <th className={TH}>Reloads</th>
          <th className={TH}>Cards</th>
          <th className={TH}>Signals</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.user.id} className={ROW}>
            <td className={TD}>
              <UserLink eventId={eventId} user={row.user} />
              <div className="text-xs text-neutral-500 dark:text-neutral-400">{row.user.email}</div>
              {row.block && <span className="text-xs text-red-600 dark:text-red-400">{row.block.scope} block · {row.block.reason}</span>}
            </td>
            <td className={TD}><ScoreBadge score={row.score} /></td>
            <td className={TD}><DecisionChip decision={row.decision} /></td>
            <td className={TD}>
              {row.reloads.count} · {money(row.reloads.amount)}
              <div className="text-xs text-neutral-500 dark:text-neutral-400">{row.reloads.count_window} in window · {money(row.reloads.amount_24h)} / 24h</div>
            </td>
            <td className={TD}>
              {row.cards.distinct}
              {row.cards.shared_with_users ? <div className="text-xs text-amber-600 dark:text-amber-400">shared with {row.cards.shared_with_users}</div> : null}
            </td>
            <td className={cn(TD, 'whitespace-normal min-w-[14rem]')}><SignalList signals={row.signals} compact /></td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function Feed({ eventId, rows, money }: { eventId: number; rows: FraudAssessment[]; money: (v: number | string) => string }) {
  if (rows.length === 0) {
    return <p className="px-6 pb-4 text-sm text-neutral-500 dark:text-neutral-400">No decisions recorded yet.</p>
  }
  return (
    <ul className="divide-y divide-neutral-100 dark:divide-neutral-800 max-h-[560px] overflow-y-auto">
      {rows.map((a) => (
        <li key={a.id} className="px-6 py-2.5 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-neutral-900 dark:text-white truncate">
              {a.user_id ? (
                <Link to="/events/$eventId/fraud/users/$userId" params={{ eventId: String(eventId), userId: String(a.user_id) }} className="text-blue-600 dark:text-blue-400 hover:underline">
                  {a.user_name || `User #${a.user_id}`}
                </Link>
              ) : 'Unknown'}
            </span>
            <span className="text-xs text-neutral-500 dark:text-neutral-400 shrink-0">{relativeTime(a.created_at)}</span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <DecisionChip decision={a.decision} />
            <OutcomeChip outcome={a.outcome} />
            <span className="text-neutral-600 dark:text-neutral-400">{kindLabel(a.kind)} · {money(a.amount)} · score {a.score}</span>
            {!a.enforced && a.decision !== 'allow' && <span className="text-xs text-neutral-400">(shadow)</span>}
          </div>
          {a.signals.length > 0 && <div className="mt-1.5"><SignalList signals={a.signals} compact /></div>}
        </li>
      ))}
    </ul>
  )
}
